import { defaultError, error } from "@api/error";
import registrationInfoRequestUpdate from "@api/operations/registration-info-request-update";
import Attribute from "@api/types/attribute";
import { CFC } from "@api/types/cypress";
import Error from "@api/types/error";
import ProfileAttribute from "@api/types/profile-attribute";
import { checked, oneChecked, required, validateWithData } from "@api/validations";
import Modal from "components/EnrolledCourseRegistrationForm/Modal";
import RegistrationDetail from "components/EnrolledCourseRegistrationForm/RegistrationDetail";
import Router from "next/router";
import React, { useState } from "react";

import Button from "@components/Button";

const InternalForm: CFC<{
  token: string;
  registrationFormData: ProfileAttribute[];
  profile: Attribute[];
  email: string;
  productCode: string;
  completionId: string;
}> = ({ token, registrationFormData, profile, email, productCode, completionId }) => {
  const getProfileAttribute = (code: string) => {
    return profile.find((a) => a.code === code);
  };

  const isLocked = (formLock: boolean, value: any, code: string) => {
    if (code === "firstName" || code === "lastName") {
      // firstName and lastName only locks if form has been completed once before
      const hasCompletedCourseRegFormAttr = getProfileAttribute("hasCompletedCourseRegForm");
      const hasCompletedCourseRegForm =
        hasCompletedCourseRegFormAttr &&
        hasCompletedCourseRegFormAttr.value &&
        hasCompletedCourseRegFormAttr.value === "true";
      return formLock && typeof value === "string" && value.length > 0 && hasCompletedCourseRegForm;
    } else {
      return formLock && typeof value === "string" && value.length > 0;
    }
  };

  const initialAttributes = (registrationFormData: ProfileAttribute[]) => {
    const tempAttributes = [];

    registrationFormData.map((data) => {
      if (data.fieldName && data.fieldName.length > 0) {
        let value = (
          getProfileAttribute(data.fieldName) ? getProfileAttribute(data.fieldName).value : ""
        ) as any;
        if (data.type === "checkBox") {
          // checkbox
          value = getProfileAttribute(data.fieldName)
            ? getProfileAttribute(data.fieldName).value === "true"
            : false;
        }

        if (data.type === "multipleCheckboxes") {
          // multiple checkboxes
          value = getProfileAttribute(data.fieldName)
            ? getProfileAttribute(data.fieldName).value.split(",")
            : [];
        }

        tempAttributes.push({
          code: data.fieldName,
          value: value,
          type: data.type,
          required: data.required,
          error: defaultError(),
          locked: isLocked(data.formLock, value, data.fieldName),
          formShow: data.formShow,
          validators: data.validators,
        });
      }
    });

    return tempAttributes;
  };

  const [attributes, setAttributes] = useState(initialAttributes(registrationFormData));
  const [formError, setFormError] = useState(defaultError());
  const [show, setShow] = useState(false);

  const mapAttribute = (attribute: any) => {
    let value = attribute.value;
    if (attribute.type === "multipleCheckboxes") {
      // multiple checkboxes
      value = attribute.value.join();
    }

    return {
      code: attribute.code,
      value: value,
    };
  };

  const onChangeAttribute = async (field: string, value: any) => {
    const tempAttributes = [...attributes];
    const attribute = tempAttributes.find((a) => a.code === field);

    // if no attribute to set, return
    if (!attribute) {
      return;
    }

    attribute.value = value;

    if (field === "country") {
      // if country is changing the state and county should be emptied
      const stateAttribute = tempAttributes.find((a) => a.code === "state");
      stateAttribute.value = "";

      const countyAttribute = tempAttributes.find((a) => a.code === "county");
      if (countyAttribute) {
        // county is a field on the form
        countyAttribute.value = "";
      }

      attribute.error = await validate(attribute);
    } else if (field === "state") {
      // if state is changing the county should be emptied
      const countyAttribute = tempAttributes.find((a) => a.code === "county");
      if (countyAttribute) {
        // county is a field on the form
        countyAttribute.value = "";
      }

      attribute.error = await validate(attribute);
    } else if (attribute.type === "select") {
      attribute.error = await validate(attribute);
    } else if (attribute.type === "checkBox" || attribute.type === "dateSelector") {
      attribute.error = await validate(attribute);
    } else if (field === "county") {
      attribute.error = await validate(attribute);
    }

    setAttributes(tempAttributes);
    setFormError(errorOnForm());
  };

  const validateOnBlurAttribute = async (field: string, validators: any[]) => {
    const tempAttributes = [...attributes];
    const attribute = tempAttributes.find((a) => a.code === field);

    if (validators !== null && validators.length > 0) {
      attribute.error = await validate(attribute, validators);
    } else {
      attribute.error = await validate(attribute);
    }

    setAttributes(tempAttributes);
    setFormError(errorOnForm());
  };

  const onMultiCheckboxChange = async (field: string, value: string, checked: boolean) => {
    const tempAttributes = [...attributes];
    const attribute = tempAttributes.find((a) => a.code === field);
    attribute.value = attribute.value.filter((v: string) => v !== value);
    if (checked) {
      attribute.value.push(value);
    }
    attribute.error = await validate(attribute);

    setAttributes(tempAttributes);
    setFormError(errorOnForm);
  };

  const save = async () => {
    if (!(await passValidation())) {
      return;
    }

    // only use attributes that are not inline text and are visible on edit profile forms
    const requestAttributes = [...attributes]
      .filter((a) => a.type !== "inlineText" && a.formShow !== false)
      .map(mapAttribute);

    // add attribute for completing course registration form
    requestAttributes.push({ code: "hasCompletedCourseRegForm", value: "true" });

    await registrationInfoRequestUpdate(token, {
      email,
      course: productCode,
      completionId,
      registrationData: requestAttributes,
    }).then(() => {
      setShow(false);
      Router.reload();
    });
  };

  const passValidation = async () => {
    // validate each attribute and set the error on it
    for (const attribute of attributes) {
      let validators = attribute.validators;
      let error = attribute.error;
      if (!error.active) {
        if (validators.length > 0) {
          error = await validate(attribute, validators);
        } else {
          error = await validate(attribute);
        }

        setAttributeError(attribute.code, error);
      }
    }

    // set error on the form
    setFormError(errorOnForm());

    return !hasErrors();
  };

  const validate = async (attribute: any, validators = null) => {
    // required validation happens first
    let errorAttr = requiredValidation(attribute);

    /*
     *  only validate these when there is a value
     */
    if (!errorAttr.active && validators !== null && attribute.value.length > 0) {
      if (attribute.code === "georgiaApplicationId") {
        let birthDate = attributes.find((el) => el.code === "birthDate").value;
        birthDate === ""
          ? (errorAttr = error("ID requires a matching birth date"))
          : (errorAttr = await validateWithData(
              attribute.code,
              {
                value: attribute.value,
                dob: birthDate,
              },
              validators
            ));
      } else errorAttr = await validateWithData(attribute.code, attribute.value, validators);
    }

    return errorAttr;
  };

  const requiredValidation = (attribute: any) => {
    // not required
    if (!attribute.required) {
      return defaultError();
    }

    if (attribute.type === "checkBox") {
      // checkbox
      return checked(attribute.value as boolean);
    } else if (attribute.type === "multipleCheckboxes") {
      // multiple checkboxes
      return oneChecked(attribute.value as string[]);
    } else {
      // other types
      return required(attribute.value);
    }
  };

  const setAttributeError = (field: string, error: Error) => {
    const tempAttributes = [...attributes];
    const attribute = tempAttributes.find((a) => a.code === field);
    attribute.error = error;

    setAttributes(tempAttributes);
  };

  const hasErrors = () => {
    return attributes.filter((a) => a.error.active === true).length > 0;
  };

  const errorOnForm = () => {
    if (hasErrors()) {
      return error("Please correct the noted errors and re-submit.");
    } else {
      return defaultError();
    }
  };

  return (
    <div>
      <Modal show={show} close={() => setShow(false)}>
        <RegistrationDetail
          onChangeAttribute={onChangeAttribute}
          onBlurAttribute={(field, validators) => validateOnBlurAttribute(field, validators)}
          onMultiCheckboxChange={onMultiCheckboxChange}
          registrationFormData={registrationFormData}
          attributes={attributes}
          error={formError}
          onSubmit={() => save()}
        />
      </Modal>
      <Button onClick={() => setShow(true)} width="250px">
        Start here
      </Button>
    </div>
  );
};

export default InternalForm;
